import type { GameState } from '../../types/simulation.js';
import { SAVE_SCHEMA_VERSION } from '../../config/game.js';
import { createSeedState } from '../../data/seed.js';
import { generateCongressState } from '../../data/institutionSeed.js';
import { createFederalismState } from '../../data/federalismSeed.js';
import { createEconomyState } from '../../data/economySeed.js';
import { createEnterpriseState } from '../../data/enterpriseSeed.js';
import { createInfrastructureState } from '../../data/infrastructureSeed.js';
import { createSocietyState } from '../../data/societySeed.js';
import { createInternalSecurityState } from '../../data/internalSecuritySeed.js';
import { createInternationalState } from '../../data/internationalSeed.js';
import { createStrategicPowerState } from '../../data/strategicSeed.js';
import { createDefenseState } from '../../data/defenseSeed.js';
import { createWarfareState } from '../../data/warfareSeed.js';
import { createPoliticsState } from '../../data/politicsSeed.js';
import { createAnalyticsState, createProceduralState, createContinuityState } from '../../data/prompt17Seed.js';

const assertFiniteNumbers = (value: unknown, path = 'root'): void => {
  if (typeof value === 'number' && !Number.isFinite(value)) throw new Error(`Valeur numérique invalide à ${path}.`);
  if (Array.isArray(value)) value.forEach((item, index) => assertFiniteNumbers(item, `${path}[${index}]`));
  else if (value && typeof value === 'object') Object.entries(value as Record<string, unknown>).forEach(([key, item]) => assertFiniteNumbers(item, `${path}.${key}`));
};

const migrate = (rawState: GameState): GameState => {
  if (rawState.schemaVersion === SAVE_SCHEMA_VERSION) return rawState;
  const seed = Number.isFinite(rawState.seed) ? rawState.seed : 20290120;
  const base = createSeedState(seed);
  if (rawState.schemaVersion === 1) {
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      clock: { ...base.clock, ...rawState.clock },
      mandate: { ...base.mandate, ...rawState.mandate, phase: rawState.mandate?.partyEnvironment === 'non_defini' ? 'configuration' : 'en_fonction' },
      presidential: base.presidential,
      congress: base.congress,
      judiciary: base.judiciary,
      administration: base.administration,
      indicators: { ...base.indicators, ...rawState.indicators },
      actors: { ...base.actors, ...rawState.actors },
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }
  if (rawState.schemaVersion === 2) {
    const party = rawState.presidential?.profile?.party === 'republicain' ? 'republicain' : 'democrate';
    const composition = rawState.presidential?.congress ?? base.presidential.congress;
    const election = rawState.presidential?.election;
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      congress: generateCongressState(seed, composition, election, party),
      judiciary: base.judiciary,
      administration: base.administration,
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }

  if (rawState.schemaVersion === 3) {
    const approval = rawState.indicators?.approval?.value ?? 51;
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      federalism: createFederalismState(rawState.clock?.currentTime ?? base.clock.currentTime, rawState.presidential?.election, rawState.presidential?.governors ?? [], rawState.congress?.members ?? [], approval),
      economy: base.economy,
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }
  if (rawState.schemaVersion === 4) {
    const federalism = rawState.federalism ?? base.federalism;
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      economy: createEconomyState(rawState.clock?.currentTime ?? base.clock.currentTime, federalism),
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }

  if (rawState.schemaVersion === 5) {
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      enterprise: createEnterpriseState(),
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }

  if (rawState.schemaVersion === 6) {
    const federalism = rawState.federalism ?? base.federalism;
    const enterprise = rawState.enterprise ?? base.enterprise;
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      infrastructure: createInfrastructureState(federalism, enterprise),
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }

  if (rawState.schemaVersion === 7) {
    const federalism = rawState.federalism ?? base.federalism;
    const enterprise = rawState.enterprise ?? base.enterprise;
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      society: createSocietyState(federalism, enterprise),
      internalSecurity: base.internalSecurity,
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }
  if (rawState.schemaVersion === 8) {
    const federalism = rawState.federalism ?? base.federalism;
    const enterprise = rawState.enterprise ?? base.enterprise;
    const infrastructure = rawState.infrastructure ?? base.infrastructure;
    const society = rawState.society ?? base.society;
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      internalSecurity: createInternalSecurityState(federalism, enterprise, infrastructure, society),
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }
  if (rawState.schemaVersion === 9) {
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      international: createInternationalState(),
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }
  if (rawState.schemaVersion === 10) {
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      strategic: createStrategicPowerState(),
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }

  if (rawState.schemaVersion === 11) {
    const federalism = rawState.federalism ?? base.federalism;
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      defense: createDefenseState(federalism),
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }
  if (rawState.schemaVersion === 12) {
    const federalism = rawState.federalism ?? base.federalism;
    const fresh = createDefenseState(federalism);
    const old = rawState.defense as any;
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      defense: {
        ...fresh,
        ...old,
        equipmentCatalog: old?.equipmentCatalog ?? fresh.equipmentCatalog,
        procurementPrograms: old?.procurementPrograms ?? [],
        industrialLines: old?.industrialLines ?? fresh.industrialLines,
        munitionStocks: old?.munitionStocks ?? fresh.munitionStocks,
        foreignMilitarySales: old?.foreignMilitarySales ?? [],
        bases: old?.bases ?? fresh.bases,
        baseProjects: old?.baseProjects ?? [],
        logistics: old?.logistics ?? fresh.logistics,
        researchSources: [...(old?.researchSources ?? []), ...fresh.researchSources.filter(x=>!(old?.researchSources ?? []).some((y:any)=>y.id===x.id))]
      },
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }

  if (rawState.schemaVersion === 13) {
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      crisis: base.crisis,
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }

  if (rawState.schemaVersion === 14) {
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      warfare: createWarfareState(),
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }

  if (rawState.schemaVersion === 15) {
    const federalism = rawState.federalism ?? base.federalism;
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      politics: createPoliticsState(federalism),
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }

  if (rawState.schemaVersion === 16) {
    const indicators = rawState.indicators ?? base.indicators;
    const clock = rawState.clock ?? base.clock;
    return {
      ...base,
      ...rawState,
      schemaVersion: SAVE_SCHEMA_VERSION,
      analytics: createAnalyticsState({indicators, clock}),
      procedural: createProceduralState(),
      continuity: createContinuityState(),
      save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION }
    };
  }

  if (rawState.schemaVersion === 17) {
    const freshInternational=createInternationalState();
    const oldIntl=(rawState as any).international??{};
    const presidential=(rawState as any).presidential??base.presidential;
    const freshCabinet=base.presidential.cabinet;
    presidential.cabinet=(presidential.cabinet??freshCabinet).map((m:any)=>{const fallback=freshCabinet.find(x=>x.departmentId===m.departmentId);return {...fallback,...m,leadershipStyle:m.leadershipStyle??fallback?.leadershipStyle};});
    presidential.nominees=(presidential.nominees??base.presidential.nominees).map((n:any)=>{const fallback=base.presidential.nominees.find(x=>x.id===n.id);return {...fallback,...n,leadershipStyle:n.leadershipStyle??fallback?.leadershipStyle};});
    return {
      ...base,
      ...rawState,
      schemaVersion:SAVE_SCHEMA_VERSION,
      presidential,
      international:{...freshInternational,...oldIntl,bilateralEdges:oldIntl.bilateralEdges??freshInternational.bilateralEdges,strategicLocations:oldIntl.strategicLocations??freshInternational.strategicLocations,disasters:oldIntl.disasters??[],conflicts:oldIntl.conflicts??[],organizations:oldIntl.organizations?.length?[...oldIntl.organizations,...freshInternational.organizations.filter(x=>!oldIntl.organizations.some((y:any)=>y.id===x.id))]:freshInternational.organizations,researchSources:[...(oldIntl.researchSources??[]),...freshInternational.researchSources.filter(x=>!(oldIntl.researchSources??[]).some((y:any)=>y.id===x.id))]},
      save:{...base.save,...rawState.save,schemaVersion:SAVE_SCHEMA_VERSION}
    } as GameState;
  }
  if (rawState.schemaVersion === 18) {
    return { ...base, ...rawState, schemaVersion: SAVE_SCHEMA_VERSION, presidentialLife: base.presidentialLife, save: { ...base.save, ...rawState.save, schemaVersion: SAVE_SCHEMA_VERSION } } as GameState;
  }
  return rawState;
};

export const serializeGameState = (state: GameState): string => {
  assertFiniteNumbers(state);
  return JSON.stringify(state);
};

export const deserializeGameState = (raw: string): GameState => {
  const parsed = JSON.parse(raw) as GameState;
  if (!parsed || typeof parsed !== 'object') throw new Error('Sauvegarde invalide.');
  if (parsed.schemaVersion > SAVE_SCHEMA_VERSION) throw new Error('Cette sauvegarde provient d’une version plus récente du jeu.');
  if (parsed.schemaVersion < 1) throw new Error('Version de sauvegarde non prise en charge.');
  if (!parsed.clock?.currentTime || Number.isNaN(Date.parse(parsed.clock.currentTime))) throw new Error('Horloge de sauvegarde invalide.');
  const migrated = migrate(parsed);
  assertFiniteNumbers(migrated);
  return migrated;
};
